// The editor's state engine: a working PostComposition + selection + undo/redo, and
// every mutation the UI needs. Pure-ish (no DOM); the visual components call into it.
//
// History model:
//   - commit(mutator): a discrete edit (recolor, add, delete) → pushes one undo step.
//   - beginGesture()/gestureUpdate(mutator)/endGesture(): a drag/resize/rotate gesture
//     → ONE undo step for the whole gesture (snapshot at begin, pushed at end if changed).
// Any mutation flips meta.dirty so the master gets re-rendered from the doc on publish.

import { useCallback, useMemo, useRef, useState } from 'react'
import type {
  AspectRatio,
  DecorLayerMark,
  Fill,
  Layer,
  LayerType,
  PostComposition,
  TextLayer,
} from '../types'
import {
  addLayer,
  clone,
  duplicateLayer as opDuplicate,
  makeDecorLayer,
  makeImageLayer,
  makeShapeLayer,
  makeTextLayer,
  mapLayer,
  moveLayerBefore,
  newLayerId,
  removeLayer,
  reorderLayers,
  resizeTextTo,
  restyleRange,
  restyleText,
  restyleTreatment,
  scaleRange,
  scaleText,
  setAspect,
  setRuns,
  setTextPreservingRuns,
  withDirty,
  type ReorderDir,
} from './composition-ops'

const MAX_HISTORY = 50

export type CompositionEditor = ReturnType<typeof useCompositionEditor>

export function useCompositionEditor(initial: PostComposition) {
  const [comp, setComp] = useState<PostComposition>(() => clone(initial))
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const past = useRef<PostComposition[]>([])
  const future = useRef<PostComposition[]>([])
  const gestureSnapshot = useRef<PostComposition | null>(null)
  const [, force] = useState(0)
  const rerender = useCallback(() => force(n => n + 1), [])

  const pushPast = useCallback((snapshot: PostComposition) => {
    past.current.push(snapshot)
    if (past.current.length > MAX_HISTORY) past.current.shift()
    future.current = []
  }, [])

  // Discrete edit: snapshot current, then apply.
  const commit = useCallback(
    (mutator: (c: PostComposition) => PostComposition) => {
      setComp(cur => {
        pushPast(cur)
        return withDirty(mutator(cur))
      })
    },
    [pushPast]
  )

  const beginGesture = useCallback(() => {
    setComp(cur => {
      gestureSnapshot.current = cur
      return cur
    })
  }, [])

  // During a gesture: mutate WITHOUT touching history (cheap, every pointer move).
  const gestureUpdate = useCallback((mutator: (c: PostComposition) => PostComposition) => {
    setComp(cur => withDirty(mutator(cur)))
  }, [])

  const endGesture = useCallback(() => {
    const snap = gestureSnapshot.current
    gestureSnapshot.current = null
    if (!snap) return
    setComp(cur => {
      if (JSON.stringify(snap) !== JSON.stringify(cur)) pushPast(snap)
      return cur
    })
  }, [pushPast])

  // Restoring a snapshot must keep the doc DIRTY even when the snapshot predates the first
  // edit (its own meta.dirty is false). The autosave effect only persists dirty docs, so a
  // plain restore of a clean snapshot would never reach the server — leaving the saved doc on
  // the pre-undo state, which then resurrects the undone edit on any refetch (post-revise,
  // reopen, or a live content:updated). Stamp dirty so undo/redo always persist what's on screen.
  const undo = useCallback(() => {
    setComp(cur => {
      const prev = past.current.pop()
      if (!prev) return cur
      future.current.push(cur)
      return withDirty(prev)
    })
    rerender()
  }, [rerender])

  const redo = useCallback(() => {
    setComp(cur => {
      const nxt = future.current.pop()
      if (!nxt) return cur
      past.current.push(cur)
      return withDirty(nxt)
    })
    rerender()
  }, [rerender])

  // --- layer mutations ----------------------------------------------------

  // Every manual mutation stamps `userEdited` so an AI revise preserves the user's
  // layout/style on this layer (the AI still owns words + imagery). See merge-composition.
  const updateLayer = useCallback(
    (id: string, patch: Partial<Layer>, opts?: { gesture?: boolean }) => {
      const apply = (c: PostComposition) =>
        mapLayer(c, id, l => ({ ...l, ...patch, userEdited: true }) as Layer)
      if (opts?.gesture) gestureUpdate(apply)
      else commit(apply)
    },
    [commit, gestureUpdate]
  )

  const updateTextLayer = useCallback(
    (id: string, patch: Partial<TextLayer>) => {
      commit(c =>
        mapLayer(c, id, l =>
          l.type === 'text' ? ({ ...l, ...patch, userEdited: true } as Layer) : l
        )
      )
    },
    [commit]
  )

  // Whole-layer text STYLE changes must also overwrite per-run overrides, or they're
  // invisible: a seeded headline is multi-run (lead/focus/trail each carry their own
  // color/fontWeight/fontFamily/fontSizePct) and the renderer resolves `run.X ?? layer.X`.
  // The run-aware transforms live (and are unit-tested) in composition-ops.
  const setTextColor = useCallback(
    (id: string, color: string) =>
      commit(c => mapLayer(c, id, l => (l.type === 'text' ? restyleText(l, { color }) : l))),
    [commit]
  )

  const setTextWeight = useCallback(
    (id: string, fontWeight: number) =>
      commit(c => mapLayer(c, id, l => (l.type === 'text' ? restyleText(l, { fontWeight }) : l))),
    [commit]
  )

  const setTextFontFamily = useCallback(
    (id: string, fontFamily: string) =>
      commit(c => mapLayer(c, id, l => (l.type === 'text' ? restyleText(l, { fontFamily }) : l))),
    [commit]
  )

  // Scale the whole headline by a factor, preserving each run's emphasis ratio.
  const scaleTextSize = useCallback(
    (id: string, factor: number) =>
      commit(c => mapLayer(c, id, l => (l.type === 'text' ? scaleText(l, factor) : l))),
    [commit]
  )

  // Set an absolute nominal layer size (inspector slider); scales runs to match.
  const setTextSize = useCallback(
    (id: string, layerPct: number) =>
      commit(c => mapLayer(c, id, l => (l.type === 'text' ? resizeTextTo(l, layerPct) : l))),
    [commit]
  )

  // Edit a text layer's words as newline-separated lines, PRESERVING each line's style
  // (color/size/weight/font). A multi-line headline keeps its per-line emphasis — editing the
  // gold "À PARIS" line stays gold + big instead of collapsing to one flat nominal size.
  // See setTextPreservingRuns; intra-line inline emphasis is editable in the Phase 2 rich editor.
  const setTextContent = useCallback(
    (id: string, text: string) => {
      commit(c => mapLayer(c, id, l => (l.type === 'text' ? setTextPreservingRuns(l, text) : l)))
    },
    [commit]
  )

  // Set a text layer's runs directly — the rich inline editor serializes its styled-span DOM
  // back to runs (preserving per-span emphasis). Called debounced by the editor, so each commit
  // is a coarse undo step (not one per keystroke).
  const setTextRuns = useCallback(
    (id: string, runs: TextLayer['runs']) =>
      commit(c => mapLayer(c, id, l => (l.type === 'text' ? setRuns(l, runs) : l))),
    [commit]
  )

  // Restyle ONLY the selected characters (rich-text span styling): color/weight/family/
  // treatments (italic/underline/highlight — false/null removes).
  const restyleTextRange = useCallback(
    (
      id: string,
      start: number,
      end: number,
      patch: {
        color?: string
        fontWeight?: number
        fontFamily?: string
        italic?: boolean
        underlineColor?: string | null
        highlightColor?: string | null
      }
    ) =>
      commit(c =>
        mapLayer(c, id, l => (l.type === 'text' ? restyleRange(l, start, end, patch) : l))
      ),
    [commit]
  )

  // Set a treatment on the WHOLE text layer (every run): italic / underline / highlight.
  const setTextTreatment = useCallback(
    (
      id: string,
      patch: { italic?: boolean; underlineColor?: string | null; highlightColor?: string | null }
    ) => commit(c => mapLayer(c, id, l => (l.type === 'text' ? restyleTreatment(l, patch) : l))),
    [commit]
  )

  // Scale ONLY the selected characters' size (A+/A− on a selection).
  const scaleTextRange = useCallback(
    (id: string, start: number, end: number, factor: number) =>
      commit(c =>
        mapLayer(c, id, l =>
          l.type === 'text' ? scaleRange(l, start, end, factor, l.fontSizePct) : l
        )
      ),
    [commit]
  )

  // Inline (on-canvas) text editing: which text layer is being typed into directly.
  // Selecting anything other than the layer currently being edited ends inline editing
  // (otherwise the editor stays open over the old layer and the new layer's toolbar is hidden).
  const select = useCallback((id: string | null) => {
    setSelectedId(id)
    setEditingId(e => (e && e !== id ? null : e))
  }, [])
  const beginTextEdit = useCallback((id: string) => {
    setSelectedId(id)
    setEditingId(id)
  }, [])
  const endTextEdit = useCallback(() => setEditingId(null), [])

  const addText = useCallback(() => {
    const id = newLayerId('text')
    commit(c => addLayer(c, makeTextLayer(c, id)))
    setSelectedId(id)
    setEditingId(id)
    return id
  }, [commit])

  const addShape = useCallback(() => {
    const id = newLayerId('shape')
    commit(c => addLayer(c, makeShapeLayer(c, id)))
    setSelectedId(id)
    return id
  }, [commit])

  // Add a hand-made accent mark (squiggle/zigzag/asterisk/arrow/circle) in the brand accent.
  const addDecor = useCallback(
    (mark: DecorLayerMark) => {
      const id = newLayerId('decor')
      commit(c => addLayer(c, makeDecorLayer(c, id, mark)))
      setSelectedId(id)
      return id
    },
    [commit]
  )

  // Add a NEW photo layer (a chosen brand/uploaded photo), distinct from replacing the background.
  const addImage = useCallback(
    (attachmentId: string) => {
      const id = newLayerId('image')
      commit(c => addLayer(c, makeImageLayer(c, id, attachmentId)))
      setSelectedId(id)
      return id
    },
    [commit]
  )

  const deleteLayer = useCallback(
    (id: string) => {
      commit(c => removeLayer(c, id))
      setSelectedId(s => (s === id ? null : s))
      setEditingId(e => (e === id ? null : e))
    },
    [commit]
  )

  const duplicateLayer = useCallback(
    (id: string) => {
      let created: string | null = null
      commit(c => {
        const { comp: next, newId } = opDuplicate(c, id)
        created = newId
        return next
      })
      if (created) setSelectedId(created)
    },
    [commit]
  )

  const reorder = useCallback(
    (id: string, dir: ReorderDir) => commit(c => reorderLayers(c, id, dir)),
    [commit]
  )

  // Drag-to-reorder in the Layers panel: move a layer to just before another (or to the end).
  const moveLayer = useCallback(
    (id: string, beforeId: string | null) => commit(c => moveLayerBefore(c, id, beforeId)),
    [commit]
  )

  const toggleHidden = useCallback(
    (id: string) => commit(c => mapLayer(c, id, l => ({ ...l, hidden: !l.hidden }))),
    [commit]
  )
  const toggleLock = useCallback(
    (id: string) => commit(c => mapLayer(c, id, l => ({ ...l, locked: !l.locked }))),
    [commit]
  )
  const setLayerFill = useCallback(
    (id: string, fill: Fill) =>
      commit(c =>
        mapLayer(c, id, l =>
          l.type === 'shape' || l.type === 'overlay'
            ? ({ ...l, fill, userEdited: true } as Layer)
            : l
        )
      ),
    [commit]
  )

  // Swap an image layer's photo to a chosen attachment. Centralizes the `srcUserChosen`
  // contract (marks the photo user-picked so an AI revise keeps it) shared by the desktop
  // floating toolbar, the mobile layer toolbar, and the inspector.
  const replaceImageSrc = useCallback(
    (id: string, attachmentId: string) =>
      updateLayer(id, { src: { kind: 'attachment', attachmentId }, srcUserChosen: true }),
    [updateLayer]
  )

  const setCanvasAspect = useCallback(
    (aspect: AspectRatio) => commit(c => setAspect(c, aspect)),
    [commit]
  )

  const replaceComposition = useCallback((next: PostComposition) => {
    past.current = []
    future.current = []
    // A gesture straddling the swap must not settle: its endGesture would push the
    // PRE-swap comp as an undo step over the replacement (a cross-slide time bomb).
    gestureSnapshot.current = null
    setComp(clone(next))
    setSelectedId(null)
    setEditingId(null)
  }, [])

  const selected = useMemo(
    () => comp.layers.find(l => l.id === selectedId) ?? null,
    [comp, selectedId]
  )
  const counts = useMemo(() => {
    const c: Partial<Record<LayerType, number>> = {}
    for (const l of comp.layers) c[l.type] = (c[l.type] ?? 0) + 1
    return c
  }, [comp])

  return {
    comp,
    selected,
    selectedId,
    editingId,
    counts,
    canUndo: past.current.length > 0,
    canRedo: future.current.length > 0,
    select,
    beginTextEdit,
    endTextEdit,
    commit,
    beginGesture,
    gestureUpdate,
    endGesture,
    undo,
    redo,
    updateLayer,
    updateTextLayer,
    setTextColor,
    setTextWeight,
    setTextFontFamily,
    scaleTextSize,
    setTextSize,
    setTextContent,
    setTextRuns,
    restyleTextRange,
    scaleTextRange,
    setTextTreatment,
    addText,
    addShape,
    addDecor,
    addImage,
    deleteLayer,
    duplicateLayer,
    reorder,
    moveLayer,
    toggleHidden,
    toggleLock,
    setLayerFill,
    replaceImageSrc,
    setCanvasAspect,
    replaceComposition,
  }
}
